import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useContent } from "@/context/ContentContext";

const makePin = (color) =>
  L.divIcon({
    className: "rtpl-pin",
    html: `<svg width="30" height="30" viewBox="0 0 24 24" fill="${color}" stroke="white" stroke-width="1.6" stroke-linejoin="round" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35))">
      <path d="M12 21s-6-5.686-6-10a6 6 0 1112 0c0 4.314-6 10-6 10z"/>
      <circle cx="12" cy="11" r="2.3" fill="white" stroke="none"/>
    </svg>`,
    iconSize: [30, 30],
    iconAnchor: [15, 29],
    popupAnchor: [0, -26],
  });

const DEALER_ICON = makePin("hsl(243 59% 56%)"); // blue
const RETAIL_ICON = makePin("#DC2626"); // red

// Small longitude offset so the two pins for the same location sit side-by-side
// rather than perfectly overlapping.
const PIN_OFFSET = 0.09;

// Restrict panning to Bangladesh only
const BD_BOUNDS = [
  [20.4, 87.9],
  [26.8, 92.8],
];

const dealerCount = (d) => Number(d.dealer_count) || 0;
const retailCount = (d) => Number(d.retail_count) || 0;

export const DealerMap = () => {
  const { content } = useContent();
  const dealers = content.dealers || [];

  return (
    <div
      className="h-[480px] w-full overflow-hidden rounded-xl border border-border shadow-sm md:h-[560px]"
      data-testid="dealer-map"
    >
      <MapContainer
        center={[23.685, 90.3563]}
        zoom={7}
        minZoom={6}
        maxBounds={BD_BOUNDS}
        maxBoundsViscosity={1.0}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%", background: "#e8eef7" }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {dealers.flatMap((d, i) => {
          const dc = dealerCount(d);
          const rc = retailCount(d);
          const lat = Number(d.lat);
          const lng = Number(d.lng);
          const markers = [];
          if (dc > 0) {
            markers.push(
              <Marker
                key={`${d.id || i}-d`}
                position={[lat, lng - PIN_OFFSET / 2]}
                icon={DEALER_ICON}
              >
                <Popup>
                  <div className="min-w-[160px]">
                    <div className="text-sm font-bold text-slate-900">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.region}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "hsl(243 59% 56%)" }} />
                      <span className="text-slate-700">Dealership</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{dc} dealers</div>
                  </div>
                </Popup>
              </Marker>
            );
          }
          if (rc > 0) {
            markers.push(
              <Marker
                key={`${d.id || i}-r`}
                position={[lat, lng + PIN_OFFSET / 2]}
                icon={RETAIL_ICON}
              >
                <Popup>
                  <div className="min-w-[160px]">
                    <div className="text-sm font-bold text-slate-900">{d.name}</div>
                    <div className="text-xs text-slate-500">{d.region}</div>
                    <div className="mt-1 flex items-center gap-1.5 text-xs font-semibold">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: "#DC2626" }} />
                      <span className="text-slate-700">Retail</span>
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{rc} retail outlets</div>
                  </div>
                </Popup>
              </Marker>
            );
          }
          return markers;
        })}
      </MapContainer>
    </div>
  );
};
